import { Account } from '@livecontrol/scheduler/model';
import { WEBPLAYER_BASE_URI, WEBPLAYER_HANDLES_GENERATED_ON, WEBPLAYER_SLUGS } from './webplayer-handles';

/*
 * The webplayer link, built here instead of read from the `webplayer_url` field: that field is resolved
 * by calling the webplayer backend for the client's handle, which is exactly what these pages avoid.
 *
 * The event's part of the URL is md5(id) cut to 8 — `Account.WebPlayer.makeURL` computes it the same way
 * the backend does. The client's handle cannot be computed, so it comes from the generated table.
 *
 * The order of the checks mirrors `EventService.getEventURL` in graphql_api, so a link built here and one
 * built there agree.
 */

export interface WebplayerLinkEvent {
  eid: string;
  event_password?: string | null;
  /** The event's own `hidden` column, not the asset's. */
  event_hidden?: boolean | null;
  origin?: string | null;
  external_url?: string | null;
  client?: { cid?: string | null } | null;
}

export interface WebplayerLink {
  /** Set only when the link can be opened; its absence is what disables the button. */
  url?: string;
  tooltip: string;
  /** True for a private event, which shows a different icon. */
  hidden: boolean;
}

const MYKEEPER = 'mykeeper';

export const webplayerLink = (event: WebplayerLinkEvent): WebplayerLink => {
  if (event.origin === MYKEEPER) {
    return event.external_url
      ? { url: event.external_url, tooltip: 'Webplayer (MyKeeper external link)', hidden: false }
      : { tooltip: 'MyKeeper event with no external URL', hidden: false };
  }

  if (event.event_hidden) {
    return { tooltip: 'Private event: the webplayer link is hidden', hidden: true };
  }

  const cid = Number(event.client?.cid);

  if (!Number.isFinite(cid)) {
    return { tooltip: 'No client on this event', hidden: false };
  }

  const slug = WEBPLAYER_SLUGS[cid];

  if (!slug) {
    /* Read through `length` so an empty generated file does not narrow this to a constant. */
    const when = WEBPLAYER_HANDLES_GENERATED_ON.length
      ? ` (list generated ${String(WEBPLAYER_HANDLES_GENERATED_ON)})`
      : '';

    return {
      tooltip: `No webplayer handle on file for client ${cid}${when} — open the full event page`,
      hidden: false
    };
  }

  /*
   * No `customUrl`: the handle export carries none, so a client on their own domain gets a
   * livecontrol.tv link rather than theirs. Accepted for now.
   */
  return {
    url: Account.WebPlayer.makeURL({
      uri: WEBPLAYER_BASE_URI,
      slug,
      event: event.eid,
      password: event.event_password ?? undefined
    }),
    tooltip: 'Webplayer',
    hidden: false
  };
};
